import { memo } from 'react';
import { type NodeProps, type Node } from '@xyflow/react';
import { makeStyles, shorthands, tokens } from '@coreai/fluentui-react';
import { ArrowRepeatAllRegular } from '@fluentui/react-icons';

export type ScopeContainerNodeData = {
  label: string;
  actionType: string;
  actionName: string;
  childNodeIds?: string[];
  containerWidth?: number;
  containerHeight?: number;
};

export type ScopeContainerNodeType = Node<ScopeContainerNodeData, 'scopeContainer'>;

const useStyles = makeStyles({
  wrapper: {
    position: 'relative',
    pointerEvents: 'none',
  },
  container: {
    boxSizing: 'border-box',
    backgroundColor: tokens.colorNeutralBackground2,
    ...shorthands.border('1px', 'dashed', tokens.colorNeutralStroke1),
    borderRadius: tokens.borderRadiusXLarge,
    pointerEvents: 'none',
    opacity: '0.6',
    transitionProperty: 'width, height',
    transitionDuration: tokens.durationFast,
    transitionTimingFunction: tokens.curveEasyEase,
  },
  header: {
    position: 'absolute',
    top: '-24px',
    left: '12px',
    fontSize: tokens.fontSizeBase200,
    color: tokens.colorNeutralForeground2,
    fontWeight: tokens.fontWeightSemibold,
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalXS,
    pointerEvents: 'none',
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    maxWidth: '300px',
  },
});

export const ScopeContainerNode = memo(({ data }: NodeProps<ScopeContainerNodeType>) => {
  const styles = useStyles();
  const width = data.containerWidth ?? 200;
  const height = data.containerHeight ?? 100;
  const labelId = `scope-label-${data.actionName}`;

  return (
    <div
      className={styles.wrapper}
      data-testid="scope-container"
      role="region"
      aria-label={`${data.label} scope container`}
      tabIndex={-1}
    >
      <div className={styles.header} data-testid="scope-container-header" id={labelId}>
        <ArrowRepeatAllRegular fontSize={12} aria-label="Loop indicator" />
        {data.label}
      </div>
      <div
        className={styles.container}
        style={{ width, height }}
        aria-labelledby={labelId}
      />
    </div>
  );
});

ScopeContainerNode.displayName = 'ScopeContainerNode';
